// Admin v2 — Eszközzárolás & biztonság (Figma ADM_17, 17b, 17c, 17d).
// Eszközök: devices kollekció (deviceId, name, type, ownerName, venueName, status, lastSeen, pinHash, recoveryHash).
// A PIN és a visszaállítási kód csak SHA-256 lenyomatként tárolódik. Beállítások: app_settings/devices.
import React, { useEffect, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { timeAgo, toDate } from '../data';
import {
  Avatar, Badge, Button, Card, ConfirmModal, Empty, Field, Input, Modal, Notice, PageHeader, SectionTitle, Segmented, Select, Spinner, Tabs, Toggle, Tone, useToast,
} from '../ui';

type Status = 'active' | 'locked' | 'stolen';
interface Device { id: string; deviceId: string; name: string; type: string; owner: string; venue: string; status: Status; lastSeen: Date | null; pinHash: string; recoveryHash: string; attempts: number }
const ST: Record<Status, { t: string; tone: Tone }> = { active: { t: 'Aktív', tone: 'green' }, locked: { t: 'Zárolva', tone: 'gold' }, stolen: { t: 'Lopás jelentve', tone: 'red' } };

async function sha(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}
const recoveryCode = () => {
  const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  const s = Array.from(a, (x) => abc[x % abc.length]).join('');
  return `${s.slice(0, 4)}-${s.slice(4, 8)}-${s.slice(8, 12)}-${s.slice(12)}`;
};

export default function DeviceLockPage() {
  const toast = useToast();
  const [tab, setTab] = useState<'devices' | 'security'>('devices');
  const [list, setList] = useState<Device[] | null>(null);
  const [edit, setEdit] = useState<Device | 'new' | null>(null);
  const [del, setDel] = useState<Device | null>(null);
  const [pinFor, setPinFor] = useState<Device | null>(null);
  const [created, setCreated] = useState<{ pin: string; code: string } | null>(null);

  useEffect(() => onSnapshot(collection(db, 'devices'), (s) => setList(s.docs.map((d) => {
    const v = d.data();
    return {
      id: d.id, deviceId: String(v.deviceId ?? d.id), name: String(v.name ?? 'Eszköz'), type: String(v.type ?? 'Dart tábla'), owner: String(v.ownerName ?? ''),
      venue: String(v.venueName ?? ''), status: (ST[v.status as Status] ? v.status : 'active') as Status, lastSeen: toDate(v.lastSeen),
      pinHash: String(v.pinHash ?? ''), recoveryHash: String(v.recoveryHash ?? ''), attempts: Number(v.failedAttempts ?? 0),
    };
  })), () => setList([])), []);

  const setStatus = async (d: Device, status: Status) => {
    try { await updateDoc(doc(db, 'devices', d.id), { status, ...(status === 'active' ? { failedAttempts: 0 } : {}), updatedAt: serverTimestamp() }); toast(status === 'locked' ? 'Eszköz zárolva.' : 'Eszköz feloldva.'); }
    catch { toast('A művelet nem sikerült.', 'red'); }
  };

  return (
    <div>
      <PageHeader title="Eszközzárolás & biztonság" sub="Eszköz regisztráció, PIN védelem és lopásvédelmi funkciók kezelése" right={<Button icon="Plus" onClick={() => setEdit('new')}>Eszköz regisztrálása</Button>} />
      <Tabs value={tab} onChange={setTab} options={[{ value: 'devices', label: 'Eszközök' }, { value: 'security', label: 'Biztonság' }]} />

      {tab === 'devices' && (
        <>
          <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
            {list === null ? <Spinner /> : (
              <table className="w-full min-w-[920px] text-left">
                <thead><tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
                  {['Eszköz', 'Típus', 'Tulajdonos', 'Helyszín', 'Utoljára látva', 'Státusz', 'Műveletek'].map((h, i) => <th key={h} className={`${i ? 'px-4' : 'pl-5 pr-4'} h-[39px] font-semibold`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {list.map((d) => (
                    <tr key={d.id} className="border-t border-[#31383D] h-[60px] text-[13px]">
                      <td className="pl-5 pr-4"><div className="flex items-center gap-2.5"><Avatar name={d.name} size={30} /><div><div className="font-semibold text-white">{d.name}</div><div className="text-[11px] text-[#8a8f99]">{d.deviceId}</div></div></div></td>
                      <td className="px-4 text-[#B8B8C3]">{d.type}</td>
                      <td className="px-4 text-[#B8B8C3]">{d.owner || '—'}</td>
                      <td className="px-4 text-[#B8B8C3]">{d.venue || '—'}</td>
                      <td className="px-4 text-[#B8B8C3]">{d.lastSeen ? timeAgo(d.lastSeen) : '—'}</td>
                      <td className="px-4"><Badge tone={ST[d.status].tone}>{ST[d.status].t}</Badge></td>
                      <td className="px-4"><div className="flex gap-2">
                        {d.status === 'active' && <Sq icon="Lock" c="#d9a514" t="Zárolás" on={() => setStatus(d, 'locked')} />}
                        {d.status !== 'active' && <Sq icon="Keyhole" c="#d9a514" t="Feloldás PIN-nel" on={() => setPinFor(d)} />}
                        <Sq icon="PencilSimpleLine" t="Szerkesztés" on={() => setEdit(d)} />
                        <Sq icon="Trash" c="#ef4444" t="Törlés" on={() => setDel(d)} />
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {list?.length === 0 && <Empty>Még nincs regisztrált eszköz.</Empty>}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4 mt-6 items-start"><SecurityCard /><PinTester devices={list ?? []} /></div>
        </>
      )}
      {tab === 'security' && <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4 items-start"><SecurityCard warn /><PinTester devices={list ?? []} /></div>}

      {edit && <DeviceModal d={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onCreated={(pin, code) => setCreated({ pin, code })} />}
      {created && (
        <Modal title="Eszköz regisztrálva" onClose={() => setCreated(null)} width={460} footer={<Button onClick={() => setCreated(null)}>Rendben, elmentettem</Button>}>
          <p className="text-[13px] text-[#B8B8C3] mb-4">Ezeket a kódokat csak most látod. Tárold biztonságos helyen.</p>
          <div className="grid gap-3">
            <div className="px-4 py-3 rounded-xl bg-[#1d232a]"><div className="text-[11px] text-[#8a8f99]">PIN</div><div className="text-[20px] font-bold text-white tracking-[0.3em]">{created.pin}</div></div>
            <div className="px-4 py-3 rounded-xl bg-[#1d232a]"><div className="text-[11px] text-[#8a8f99]">Visszaállítási kód</div><div className="text-[17px] font-bold text-white font-mono">{created.code}</div></div>
          </div>
        </Modal>
      )}
      {pinFor && <PinModal d={pinFor} onClose={() => setPinFor(null)} onUnlock={() => setStatus(pinFor, 'active')} />}
      {del && <ConfirmModal title="Eszköz törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)} text={<>Biztosan törlöd: <b className="text-white">{del.name}</b>?</>}
        onConfirm={async () => { try { await deleteDoc(doc(db, 'devices', del.id)); toast('Eszköz törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); } setDel(null); }} />}
    </div>
  );
}

function Sq({ icon, c, t, on }: { icon: string; c?: string; t: string; on: () => void }) {
  return <button type="button" title={t} aria-label={t} onClick={on} className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] hover:border-[#00AFF2]/50" style={{ color: c ?? '#B8B8C3' }}><Icon name={icon} size={15} /></button>;
}

function SecurityCard({ warn }: { warn?: boolean }) {
  const toast = useToast();
  const [s, setS] = useState({ autoLock: '15', pinOnBoot: true, remoteWipe: false });
  useEffect(() => onSnapshot(doc(db, 'app_settings', 'devices'), (d) => setS((x) => ({ ...x, ...(d.data() ?? {}) })), () => undefined), []);
  const save = (patch: Partial<typeof s>) => { const n = { ...s, ...patch }; setS(n); setDoc(doc(db, 'app_settings', 'devices'), n, { merge: true }).then(() => toast('Beállítás mentve.')).catch(() => toast('A mentés nem sikerült.', 'red')); };
  return (
    <Card className="!p-5">
      <SectionTitle>Biztonsági beállítások</SectionTitle>
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-[#1d232a] flex-wrap">
          <div><div className="text-[13px] font-semibold text-white">Automatikus zárolás időkorlátja</div><div className="text-[11px] text-[#8a8f99]">Eszköz automatikus zárolása inaktivitás után</div></div>
          <Segmented size="sm" value={s.autoLock} onChange={(v) => save({ autoLock: v })} options={[{ value: '5', label: '5 perc' }, { value: '15', label: '15 perc' }, { value: '30', label: '30 perc' }, { value: 'never', label: 'Soha' }]} />
        </div>
        {([['pinOnBoot', 'PIN kötelező indításkor', 'Az eszköznek minden indításkor meg kell adni a PIN-t'], ['remoteWipe', 'Távoli törlés engedélyezése', 'Távoli adattörlés engedélyezése lopás esetén']] as const).map(([k, t, sub]) => (
          <div key={k} className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-[#1d232a]"><div><div className="text-[13px] font-semibold text-white">{t}</div><div className="text-[11px] text-[#8a8f99]">{sub}</div></div><Toggle checked={s[k]} onChange={(v) => save({ [k]: v })} /></div>
        ))}
        {warn && <div className="mt-2"><Notice>Tartsd biztonságos helyen a visszaállítási kódot. Ha a PIN és a visszaállítási kód is elvész, az eszköz nem oldható fel.</Notice></div>}
      </div>
    </Card>
  );
}

function PinTester({ devices }: { devices: Device[] }) {
  const [id, setId] = useState('');
  const [d, setD] = useState<Device | null>(null);
  const [res, setRes] = useState<boolean | null>(null);
  return (
    <Card className="!p-5">
      <SectionTitle>PIN ellenőrzés tesztelése</SectionTitle>
      <p className="text-[13px] text-[#B8B8C3] mb-3">Teszteld a PIN ellenőrzési rendszert egy eszköz azonosítóval.</p>
      <Field label="Eszköz azonosító"><Input value={id} onChange={(e) => { setId(e.target.value); setRes(null); }} placeholder="DQ-2024-001" /></Field>
      <button type="button" disabled={!id.trim()} onClick={() => { const f = devices.find((x) => x.deviceId.toLowerCase() === id.trim().toLowerCase()); if (f) setD(f); else setRes(false); }}
        className="mt-3 w-full h-[38px] rounded-full border border-[#00AFF2] text-[#00AFF2] text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"><Icon name="ShieldStar" size={15} />Ellenőrzés tesztelése</button>
      {res !== null && (
        <div className={`mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-[13px] ${res ? 'bg-[#2bc48a]/[0.12] text-[#2bc48a]' : 'bg-[#ef4444]/[0.12] text-[#ef4444]'}`}>
          <Icon name={res ? 'CheckCircle' : 'Warning'} size={16} />{res ? 'PIN ellenőrzés sikeres!' : 'Ismeretlen eszköz vagy sikertelen ellenőrzés.'}
        </div>
      )}
      {d && <PinModal d={d} test onClose={() => setD(null)} onUnlock={() => setRes(true)} onFail={() => setRes(false)} />}
    </Card>
  );
}

function PinModal({ d, test, onClose, onUnlock, onFail }: { d: Device; test?: boolean; onClose: () => void; onUnlock: () => void; onFail?: () => void }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [left, setLeft] = useState(3);
  const [msg, setMsg] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const check = async () => {
    const ok = d.pinHash && (await sha(digits.join(''))) === d.pinHash;
    if (ok) { onUnlock(); onClose(); return; }
    const l = left - 1;
    setLeft(l); setDigits(['', '', '', '', '', '']); refs.current[0]?.focus();
    if (!test) updateDoc(doc(db, 'devices', d.id), { failedAttempts: d.attempts + (3 - l) }).catch(() => undefined);
    if (l <= 0) { setMsg('Elfogytak a próbálkozások. Használd a visszaállítási kódot.'); setRecovery(true); onFail?.(); }
    else setMsg(`Helytelen PIN. Még ${l} próbálkozás maradt.`);
  };
  const checkCode = async () => {
    const ok = d.recoveryHash && (await sha(code.trim().toUpperCase())) === d.recoveryHash;
    if (ok) { onUnlock(); onClose(); } else { setMsg('Érvénytelen visszaállítási kód.'); onFail?.(); }
  };
  return (
    <Modal title="" onClose={onClose} width={440}>
      <div className="text-center -mt-6">
        <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${recovery ? 'bg-[#d9a514]/[0.14] text-[#d9a514]' : 'bg-[#00AFF2]/[0.14] text-[#00AFF2]'}`}><Icon name={recovery ? 'Info' : 'Lock'} size={22} /></span>
        <h3 className="mt-3 text-[17px] font-bold text-white">{recovery ? 'Visszaállítási kód megadása' : 'Eszköz PIN megadása'}</h3>
        <p className="mt-1.5 text-[13px] text-[#B8B8C3]">{recovery ? 'Add meg a 16 karakteres visszaállítási kódot ehhez az eszközhöz' : 'Add meg a 6 jegyű PIN kódot az eszköz feloldásához'}</p>
      </div>
      {!recovery ? (
        <>
          <div className="flex justify-center gap-2 mt-5">
            {digits.map((x, i) => (
              <input key={i} ref={(el) => { refs.current[i] = el; }} value={x} inputMode="numeric" maxLength={1} type="password"
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(-1); setDigits((a) => a.map((y, j) => (j === i ? v : y))); if (v && i < 5) refs.current[i + 1]?.focus(); }}
                onKeyDown={(e) => { if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus(); }}
                className="w-11 h-12 rounded-xl bg-[#1d232a] border border-[#31383D] focus:border-[#00AFF2] text-center text-[20px] text-white outline-none" />
            ))}
          </div>
          {msg && <p className="mt-3 text-center text-[12px] text-[#ef4444]">{msg}</p>}
          <button type="button" disabled={digits.some((x) => !x)} onClick={check} className="mt-4 w-full h-[40px] rounded-full bg-[#00AFF2] text-white text-[14px] font-semibold disabled:opacity-50">Ellenőrzés</button>
          <button type="button" onClick={() => { setRecovery(true); setMsg(''); }} className="mt-3 w-full text-center text-[12px] font-semibold text-[#00AFF2] hover:underline">Elfelejtetted a PIN-t? Használd a visszaállítási kódot</button>
        </>
      ) : (
        <>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="XXXX-XXXX-XXXX-XXXX" className="mt-5 w-full h-[46px] rounded-xl bg-[#1d232a] border border-[#31383D] focus:border-[#00AFF2] text-center text-[16px] font-bold font-mono text-white outline-none" />
          <div className="mt-1.5 text-[11px] text-[#8a8f99]">Formátum: XXXX-XXXX-XXXX-XXXX</div>
          {msg && <p className="mt-2 text-center text-[12px] text-[#ef4444]">{msg}</p>}
          <div className="grid grid-cols-2 gap-3 mt-4"><Button kind="ghost" onClick={() => { setRecovery(false); setMsg(''); }}>Vissza</Button><Button onClick={checkCode}>Ellenőrzés</Button></div>
        </>
      )}
    </Modal>
  );
}

function DeviceModal({ d, onClose, onCreated }: { d: Device | null; onClose: () => void; onCreated: (pin: string, code: string) => void }) {
  const toast = useToast();
  const [f, setF] = useState({ deviceId: d?.deviceId ?? `DQ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900) + 100)}`, name: d?.name ?? '', type: d?.type ?? 'Dart tábla', owner: d?.owner ?? '', venue: d?.venue ?? '', status: d?.status ?? 'active' as Status });
  const [pin, setPin] = useState('');
  const save = async () => {
    if (!f.name.trim() || !f.deviceId.trim()) { toast('Az eszköz neve és azonosítója kötelező.', 'red'); return; }
    if (!d && !/^\d{6}$/.test(pin)) { toast('Adj meg egy 6 jegyű PIN-t.', 'red'); return; }
    const data = { deviceId: f.deviceId.trim(), name: f.name.trim(), type: f.type, ownerName: f.owner.trim(), venueName: f.venue.trim(), status: f.status, updatedAt: serverTimestamp() };
    try {
      if (d) { await updateDoc(doc(db, 'devices', d.id), { ...data, ...(pin ? { pinHash: await sha(pin) } : {}) }); toast('Eszköz mentve.'); onClose(); }
      else {
        const code = recoveryCode();
        await addDoc(collection(db, 'devices'), { ...data, pinHash: await sha(pin), recoveryHash: await sha(code), failedAttempts: 0, lastSeen: null, createdAt: serverTimestamp() });
        onCreated(pin, code); onClose();
      }
    } catch { toast('A mentés nem sikerült.', 'red'); }
  };
  return (
    <Modal title={d ? 'Eszköz szerkesztése' : 'Eszköz regisztrálása'} onClose={onClose} width={520} footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button onClick={save}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Eszköz neve"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="pl. Fő Dart Tábla" /></Field>
        <Field label="Azonosító"><Input value={f.deviceId} onChange={(e) => setF({ ...f, deviceId: e.target.value })} /></Field>
        <Field label="Típus"><Select value={f.type} onChange={(v) => setF({ ...f, type: v })} options={['Dart tábla', 'Táblagép', 'Mobil', 'Kamera'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Státusz"><Select value={f.status} onChange={(v) => setF({ ...f, status: v as Status })} options={Object.entries(ST).map(([k, v]) => ({ value: k, label: v.t }))} /></Field>
        <Field label="Tulajdonos"><Input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} /></Field>
        <Field label="Helyszín"><Input value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} /></Field>
        <Field label={d ? 'Új PIN (opcionális)' : '6 jegyű PIN'} className="col-span-2"><Input inputMode="numeric" type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" /></Field>
      </div>
    </Modal>
  );
}
